import { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  historyAPI,
  HistoryPortalData,
  HistoryArticleCard,
} from '../../services/api';
import {
  BookOpen,
  ChevronRight,
  Loader2,
  Landmark,
} from 'lucide-react';
import BookmarkButton from '../../components/BookmarkButton';
import BookmarkBatchProvider from '../../components/BookmarkBatchProvider';

function articleUrl(article: HistoryArticleCard): string {
  return historyAPI.getArticleHtmlUrl(article.id);
}

// --- Section image map ---
const SECTION_IMAGES: Record<string, string> = {
  crises_market_events: '/images/history-crises.jpg',
  deals_trades_investing: '/images/history-deals.jpg',
  companies_corporate: '/images/history-companies.jpg',
  people_legends: '/images/history-people.jpg',
  ideas_money_institutions: '/images/history-ideas.jpg',
  policy_power_global: '/images/history-policy.jpg',
};

// --- Lead Story ---
function LeadStory({ article }: { article: HistoryArticleCard }): ReactElement {
  return (
    <div className="group block relative">
      <div className="absolute top-3 right-3 z-10 bg-black/40 backdrop-blur-sm rounded-lg">
        <BookmarkButton
          itemType="history"
          itemId={article.id}
          title={article.headline}
          subtitle={article.era ?? undefined}
          url={articleUrl(article)}
          size="md"
          className="text-white hover:text-amber-300"
        />
      </div>
      <a href={articleUrl(article)} className="block">
        <div className="relative rounded-xl overflow-hidden mb-4">
          <img
            src="/images/history-hero.jpg"
            alt="Financial History"
            className="w-full h-48 sm:h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/60 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
            {article.era && (
              <span className="text-[11px] font-medium text-amber-400 mb-1 block">{article.era}</span>
            )}
            <h2
              className="text-lg sm:text-2xl font-bold text-white group-hover:text-amber-300 transition-colors leading-snug"
              style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
            >
              {article.headline}
            </h2>
          </div>
        </div>
        {article.summary && (
          <p className="text-sm text-stone-400 leading-relaxed mb-2 line-clamp-3">
            {article.summary}
          </p>
        )}
        <span className="text-xs text-stone-500">{article.time_ago}</span>
      </a>
    </div>
  );
}

// --- Feed Row ---
function FeedRow({ article }: { article: HistoryArticleCard }): ReactElement {
  return (
    <div className="group relative flex items-start gap-3 p-3 sm:p-3.5 rounded-lg bg-stone-750 border border-stone-700 hover:border-stone-600 hover:bg-stone-700 transition-all duration-150">
      <a href={articleUrl(article)} className="flex-1 min-w-0 block">
        <h3
          className="text-[13px] sm:text-sm font-semibold text-stone-200 group-hover:text-white transition-colors leading-snug line-clamp-2 mb-1"
          style={{ fontFamily: 'Georgia, serif' }}
        >
          {article.headline}
        </h3>
        <div className="flex items-center gap-2 text-[10px] text-stone-500">
          {article.era && (
            <>
              <span className="text-amber-500">{article.era}</span>
              <span>&middot;</span>
            </>
          )}
          <span>{article.time_ago}</span>
        </div>
      </a>
      <BookmarkButton
        itemType="history"
        itemId={article.id}
        title={article.headline}
        subtitle={article.era ?? undefined}
        url={articleUrl(article)}
        size="sm"
        className="text-stone-500 hover:text-amber-400 hover:bg-stone-700/50"
      />
    </div>
  );
}

// --- Section Row (compact) ---
function SectionRow({ article }: { article: HistoryArticleCard }): ReactElement {
  return (
    <div className="group flex items-start gap-2.5 py-2.5">
      <a href={articleUrl(article)} className="flex-1 min-w-0 block">
        <h3
          className="text-[13px] font-medium text-stone-300 group-hover:text-white transition-colors leading-snug line-clamp-2"
          style={{ fontFamily: 'Georgia, serif' }}
        >
          {article.headline}
        </h3>
        <div className="flex items-center gap-1.5 mt-1 text-[10px] text-stone-500">
          {article.era && (
            <>
              <span className="text-amber-500/70">{article.era}</span>
              <span>&middot;</span>
            </>
          )}
          <span>{article.time_ago}</span>
        </div>
      </a>
      <BookmarkButton
        itemType="history"
        itemId={article.id}
        title={article.headline}
        subtitle={article.era ?? undefined}
        url={articleUrl(article)}
        size="sm"
        className="text-stone-500 hover:text-amber-400 hover:bg-stone-700/50"
      />
    </div>
  );
}

// --- Section Block ---
function SectionBlock({ sectionKey, data }: { sectionKey: string; data: { name: string; articles: HistoryArticleCard[]; total_count: number } }): ReactElement {
  if (data.articles.length === 0) return <></>;

  const imageUrl = SECTION_IMAGES[sectionKey];

  return (
    <div>
      {/* Section image header */}
      {imageUrl && (
        <div className="relative rounded-lg overflow-hidden mb-3">
          <img
            src={imageUrl}
            alt={data.name}
            className="w-full h-28 sm:h-36 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 px-3 pb-2.5">
            <h2 className="text-xs font-bold text-white uppercase tracking-wider">
              {data.name}
            </h2>
          </div>
        </div>
      )}
      {!imageUrl && (
        <div className="flex items-center justify-between mb-2 pb-2 border-b border-stone-700">
          <h2 className="text-xs font-bold text-stone-400 uppercase tracking-wider">
            {data.name}
          </h2>
        </div>
      )}
      {data.total_count > data.articles.length && (
        <div className="flex justify-end mb-2">
          <Link
            to={`/history/section/${sectionKey}`}
            className="flex items-center gap-0.5 text-xs text-amber-500 hover:text-amber-400 font-medium transition-colors"
          >
            More
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}
      <div className="divide-y divide-stone-700/50">
        {data.articles.map((article) => (
          <SectionRow key={article.id} article={article} />
        ))}
      </div>
    </div>
  );
}

// --- Section order ---
const SECTION_ORDER = [
  'crises_market_events',
  'deals_trades_investing',
  'companies_corporate',
  'people_legends',
  'ideas_money_institutions',
  'policy_power_global',
];

// --- Main Portal ---
export default function HistoryPortal(): ReactElement {
  const { data: portalData, isLoading, error } = useQuery<HistoryPortalData>({
    queryKey: ['history-portal'],
    queryFn: async () => {
      const response = await historyAPI.getPortalData(15);
      return response.data;
    },
    staleTime: 0,
  });

  const sectionKeys = portalData
    ? SECTION_ORDER.filter((k) => portalData.sections[k]?.articles?.length)
    : [];

  // Collect every history article visible on the page so the batch endpoint
  // checks them all in one round-trip instead of one /check per card.
  const batchItems: { itemType: 'history'; itemId: string }[] = portalData
    ? [
        ...(portalData.lead ? [{ itemType: 'history' as const, itemId: portalData.lead.id }] : []),
        ...portalData.feed.map((a) => ({ itemType: 'history' as const, itemId: a.id })),
        ...sectionKeys.flatMap((k) =>
          portalData.sections[k].articles.map((a) => ({ itemType: 'history' as const, itemId: a.id }))
        ),
      ]
    : [];

  return (
    <BookmarkBatchProvider items={batchItems}>
    <div className="min-h-screen bg-stone-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">

        {/* Page Header */}
        <header className="mb-6 sm:mb-8 pb-4 sm:pb-5 border-b border-stone-700">
          <div className="flex items-center gap-3">
            <Landmark className="w-5 h-5 text-amber-500" />
            <h1
              className="text-base sm:text-lg font-bold text-stone-200"
              style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
            >
              Financial History
            </h1>
          </div>
        </header>

        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500 mb-3" />
            <p className="text-sm text-stone-500">Loading history...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-center py-20">
            <BookOpen className="w-8 h-8 text-red-400 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-stone-300 mb-2">Failed to load history</h3>
            <p className="text-sm text-stone-500">Please try again later</p>
          </div>
        )}

        {/* Portal Content */}
        {portalData && !isLoading && (
          <>
            {/* Top: Lead (left) + Latest feed (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-5 mb-6 sm:mb-8">
              <div className="lg:col-span-3">
                {portalData.lead && <LeadStory article={portalData.lead} />}
                {portalData.feed.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-4">
                    {portalData.feed.slice(0, 6).map((article) => (
                      <FeedRow key={`sub-${article.id}`} article={article} />
                    ))}
                  </div>
                )}
              </div>

              <div className="lg:col-span-2 space-y-2">
                {portalData.feed.slice(6, 12).map((article) => (
                  <FeedRow key={`side-${article.id}`} article={article} />
                ))}
              </div>
            </div>

            {/* Section Lists */}
            {sectionKeys.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 pt-5 sm:pt-6 border-t border-stone-700">
                {sectionKeys.map((sectionKey) => {
                  const sectionData = portalData.sections[sectionKey];
                  if (!sectionData || sectionData.articles.length === 0) return null;
                  return (
                    <SectionBlock
                      key={sectionKey}
                      sectionKey={sectionKey}
                      data={sectionData}
                    />
                  );
                })}
              </div>
            )}

            {/* Empty State */}
            {portalData.total_articles === 0 && (
              <div className="text-center py-20">
                <BookOpen className="w-8 h-8 text-stone-600 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-stone-400 mb-2">No articles yet</h3>
                <p className="text-sm text-stone-500">Check back later</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </BookmarkBatchProvider>
  );
}
